/**
 * `<ProDocReview>` — the **Review** capability's package surface (ADR-0010),
 * the fourth of the four: the documents waiting for a person's decision,
 * oldest request first, and the decision panel — the file beside the record a
 * person gave it, an optional comment, *Approve* and *Reject* (FR-21).
 *
 * **What it is not** (ADR-0008): v1 reads nothing out of a document, so there
 * is no extraction panel, no fields and no checks. The reviewer compares the
 * file with its record — title, type, the date on it, notes, custom fields —
 * and decides. The prototype's checklist, confidence scores and arithmetic
 * checks were the parser's, and they are gone with it.
 *
 * **The rules it shows, which the service enforces** (plan D12, D14):
 *
 * - Deciding is `can_update` on `document` — a reviewer or an administrator.
 *   Without it the component says so instead of offering buttons that 403.
 * - **Four-eyes** (FR-23): with the organisation's switch on, nobody approves
 *   the version they uploaded. Their own uploads stay in the queue, marked
 *   *Yours*, and are **left out of the walk** — the queue asks the service for
 *   the walk (`exclude_own=true`) rather than guessing whose a version is. A
 *   refusal still reads as one (`FOUR_EYES`), because the service decides.
 *   **W22 (FR-23 refined, D59): the switch is off by default**, and then the
 *   walk takes the caller's own uploads too. Whether it is on comes with the
 *   queue's own read (`meta.four_eyes`), so no settings read is needed; only a
 *   service that does not say it is asked for the settings, as before.
 * - **No reopen** (D14): the prototype's *Send back for review* and its Undo
 *   do not come across. A later version is the only way back into review
 *   (FR-22), so a rejection says so.
 *
 * **Keyboard**: Ctrl/⌘ + Enter approves and opens the next; Alt + ← / → walks
 * the queue, and so do J / K (W24, FR-56) — never while typing in the comment.
 *
 * It takes the host's `apiUrl`, `fetcher` and `orgId` like its siblings and
 * imports nothing of the host's: the open document is the host's to keep
 * (`documentId` / `onSelect` — this app keeps it in `?doc=`), and the way to a
 * document's own screen is the host's (`onOpenDocument`).
 */
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DescriptionList,
  Detail,
  Dialog,
  EmptyState,
  RadioGroup,
  Skeleton,
  SkeletonRegion,
  SkeletonText,
  TONE_TEXT,
  Textarea,
  cn,
  formatDate,
  formatDateTime,
  formatNumber,
  useToast,
  type ButtonProps,
} from '@fiveox/ui'
import { ArrowLeft, ChevronLeft, ChevronRight, CircleCheck, PanelLeftClose, PanelLeftOpen, RotateCcw, TriangleAlert, UserRound } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import {
  ApiError,
  FOUR_EYES_DEFAULT,
  MAX_REVIEW_COMMENT,
  documentTypeLabel,
  formatBytes,
  fourEyesOf,
  isFourEyes,
  isIllegalTransition,
  isOwnVersion,
  mayReview,
  type CustomValue,
  type DocumentTypeOption,
  type DocumentVersion,
  type ProDocClient,
  type ProDocDocument,
} from './prodoc-client'
import { FileMark, ReviewBadge, mediaLabel, plural, reviewLabel, when } from './bits'
import { refusalText } from './document-detail'
import { Preview } from './preview'
import { uploaderLabel, useLoad, useProDocAccess, useProDocClient, useQueueKeys, useWorkspaces, versionUploaderLabel, type HostProps } from './hooks'

export interface ProDocReviewProps extends HostProps {
  /**
   * Narrow the queue to one app's records — a sibling reviewing its own
   * documents (FR-35; ProGST's bank statements). Both of the queue's questions
   * carry it, so the four-eyes walk stays inside it too.
   */
  ownerApp?: string
  /** …and to one record of that app. */
  sourceRef?: string
  /**
   * The open document — **controlled** when given (the host keeps it in its
   * URL); `null` shows the queue. Omit it and the component keeps its own.
   */
  documentId?: string | null
  /** A document opened from the queue or the walk, or `null` for the queue. */
  onSelect?: (documentId: string | null) => void
  /** "Open the whole record" — the host's way to the document's own screen. Not offered without it. */
  onOpenDocument?: (document: ProDocDocument) => void
  /** After an approval or a rejection, with the document as it now stands. */
  onDecided?: (document: ProDocDocument) => void
  /** Offered when nothing is waiting — the host's way to its documents. */
  emptyAction?: ReactNode
  className?: string
}

/** The queue is read in one page: a reviewer decides the oldest first, and the rest follow as these are decided. */
export const QUEUE_LIMIT = 100

/** Whether the queue stays beside an open document — the reviewer's choice, remembered in this browser. */
const QUEUE_KEY = 'prodoc.review.queue'

const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform)
const SHORTCUT = `${isMac ? '⌘' : 'Ctrl'} + Enter`

interface Queue {
  items: ProDocDocument[]
  total: number
  /** The ids the four-eyes walk keeps: every waiting document whose current version the caller did not upload. */
  others: Set<string>
  /** W22 (D59): the organisation's effective switch, from the list's `meta` — `null` from a service that does not say. */
  fourEyes: boolean | null
}

/**
 * The queue and its four-eyes walk, asked of the service together: the whole
 * queue (`review_state=needs_review`, oldest request first) and the same list
 * without the caller's own versions (`exclude_own=true`). A document in the
 * first and not the second is the caller's own — the service's answer, not a
 * guess from the first uploader. A host's `ownerApp` / `sourceRef` scope both.
 */
async function loadQueue(client: ProDocClient, scope: { ownerApp?: string; sourceRef?: string } = {}): Promise<Queue> {
  const question = {
    review_state: 'needs_review',
    sort: 'review_requested_at',
    dir: 'asc',
    limit: QUEUE_LIMIT,
    owner_app: scope.ownerApp,
    source_ref: scope.sourceRef,
  } as const
  const [all, others] = await Promise.all([client.documents.list(question), client.documents.list({ ...question, exclude_own: true })])
  return { items: all.items, total: all.total, others: new Set(others.items.map((d) => d.id)), fourEyes: all.four_eyes ?? others.four_eyes ?? null }
}

function readPinned(): boolean {
  try {
    return localStorage.getItem(QUEUE_KEY) === 'open'
  } catch {
    return false
  }
}

function writePinned(open: boolean): void {
  try {
    localStorage.setItem(QUEUE_KEY, open ? 'open' : 'closed')
  } catch {
    /* remembered for this visit only */
  }
}

export function ProDocReview(props: ProDocReviewProps) {
  const { ownerApp, sourceRef, onSelect, onOpenDocument, onDecided, emptyAction, className, userId } = props
  const client = useProDocClient(props)
  const access = useProDocAccess(client)
  const workspaces = useWorkspaces(client)
  const options = useLoad(() => client.documents.options(), [client])
  const [reloads, setReloads] = useState(0)
  const queue = useLoad(() => loadQueue(client, { ownerApp, sourceRef }), [client, reloads, ownerApp, sourceRef])
  // D59: the queue's read says whether four-eyes is on; only a service that does not is asked for the settings.
  const unsaid = queue.data !== null && queue.data.fourEyes === null
  const settings = useLoad(() => (unsaid ? client.settings.get() : Promise.resolve(null)), [client, unsaid])
  const [internal, setInternal] = useState<string | null>(null)
  const controlled = props.documentId !== undefined
  const selected = controlled ? (props.documentId ?? null) : internal
  // Decided here, so they leave the list before the next read says so.
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  const [settled, setSettled] = useState(0)
  const [pinned, setPinned] = useState(readPinned)

  // A fresh read is the truth: anything hidden is either gone from it or back for a reason (a new version).
  useEffect(() => setHidden(new Set()), [queue.data])

  // The queue's own answer first (D59); then the settings; until either is read, the side the service refuses on —
  // so the walk never offers what might be refused, even though the shipped default is now off.
  const fourEyes = queue.data?.fourEyes ?? (settings.data ? fourEyesOf(settings.data) : unsaid && !settings.loading ? FOUR_EYES_DEFAULT : true)
  const types = options.data?.document_types ?? []

  const rows = useMemo(() => (queue.data?.items ?? []).filter((d) => !hidden.has(d.id)), [queue.data, hidden])
  // Own, under four-eyes: the service left it out of the walk, or the row says
  // the caller uploaded its current version — either is enough to keep the
  // walk off it (a host that passes no `userId` still gets the service's answer).
  const ownOf = useCallback(
    (row: ProDocDocument) => fourEyes && ((queue.data ? !queue.data.others.has(row.id) : false) || isOwnVersion(row, userId)),
    [fourEyes, queue.data, userId],
  )
  const walk = useMemo(() => rows.filter((d) => !ownOf(d)).map((d) => d.id), [rows, ownOf])

  const select = useCallback(
    (id: string | null) => {
      onSelect?.(id)
      if (!controlled) setInternal(id)
    },
    [onSelect, controlled],
  )

  const refresh = () => setReloads((n) => n + 1)

  const decided = (doc: ProDocDocument) => {
    setHidden((prev) => new Set(prev).add(doc.id))
    setSettled((n) => n + 1)
    onDecided?.(doc)
  }

  const togglePinned = () =>
    setPinned((was) => {
      writePinned(!was)
      return !was
    })

  if (access.loading) {
    return (
      <SkeletonRegion label="Loading the review queue" className={cn('space-y-3', className)}>
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-72 rounded-2xl" />
      </SkeletonRegion>
    )
  }

  if (!mayReview(access.can)) {
    return (
      <EmptyState
        title="You can’t review documents here"
        description="Approving and rejecting is for a reviewer or an administrator — your ProDoc role in this organisation does not include it. An administrator can change roles in the FiveOX console."
        className={className}
      />
    )
  }

  /* ------------------------------------------------- a document is open */

  if (selected) {
    const side = pinned && rows.length > 0
    const queued = rows.find((d) => d.id === selected)
    return (
      <div className={cn('grid items-start gap-6', side && 'lg:grid-cols-[16rem_minmax(0,1fr)]', className)}>
        {side && (
          <aside aria-label="Waiting for review" className="max-lg:hidden lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:pb-2">
            <div className="mb-2 flex min-h-8 items-center justify-between gap-2 px-1">
              <h2 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                Waiting <span className="font-normal tracking-normal normal-case">· {rows.length}</span>
              </h2>
              <Button variant="ghost" size="sm" onClick={refresh} aria-label="Refresh the queue" title="Refresh">
                <RotateCcw size={14} aria-hidden />
              </Button>
            </div>
            <ul className="space-y-1">
              {rows.map((row) => (
                <QueueRow key={row.id} row={row} own={ownOf(row)} selected={row.id === selected} userId={userId} onOpen={select} />
              ))}
            </ul>
          </aside>
        )}

        <section aria-label="Open document" className="min-w-0">
          <DecisionPanel
            key={selected}
            client={client}
            documentId={selected}
            walk={walk}
            ownByQueue={queued ? ownOf(queued) : false}
            fourEyes={fourEyes}
            userId={userId}
            types={types}
            nameOf={workspaces.nameOf}
            onOpen={select}
            onFinished={() => select(null)}
            onDecided={decided}
            onStale={refresh}
            onOpenDocument={onOpenDocument}
            leading={
              <>
                <Button variant="ghost" size="sm" onClick={() => select(null)}>
                  <ArrowLeft size={14} aria-hidden />
                  All waiting{queue.data ? ` (${rows.length})` : ''}
                </Button>
                {rows.length > 0 && (
                  <Button
                    variant={side ? 'secondary' : 'ghost'}
                    size="sm"
                    className="max-lg:hidden"
                    onClick={togglePinned}
                    aria-pressed={side}
                    title={side ? 'Hide the queue' : 'Show the queue beside the document'}
                  >
                    {side ? <PanelLeftClose size={16} aria-hidden /> : <PanelLeftOpen size={16} aria-hidden />}
                    Queue
                  </Button>
                )}
              </>
            }
          />
        </section>
      </div>
    )
  }

  /* --------------------------------------------- nothing open: the queue */

  if (!queue.data) {
    if (queue.error) {
      return (
        <EmptyState
          title="The review queue could not be loaded"
          description={queue.error}
          action={
            <Button variant="outline" onClick={() => void queue.reload()}>
              Try again
            </Button>
          }
          className={className}
        />
      )
    }
    return (
      <SkeletonRegion label="Loading the review queue" className={cn('space-y-3', className)}>
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-72 rounded-2xl" />
      </SkeletonRegion>
    )
  }

  const stale = queue.error ? (
    <Alert tone="error" className="mb-3">
      Could not refresh the review queue: {queue.error}
    </Alert>
  ) : null

  if (rows.length === 0) {
    return (
      <div className={className}>
        {stale}
        <EmptyState
          title={settled ? 'All caught up' : 'Nothing waiting for review'}
          description={
            settled
              ? `You decided ${plural(settled, 'document')}. Nothing else is waiting.`
              : 'A document waits here when it is uploaded under a type this organisation reviews, or when someone asks for a review. Each new version of an approved or rejected document comes back here too.'
          }
          action={emptyAction}
        />
      </div>
    )
  }

  const mine = rows.length - walk.length
  const more = queue.data.total - queue.data.items.length
  return (
    <div className={className}>
      {stale}
      <Card>
        {/* The card's own header, not the kit's CardHeader: on a phone the way
            in drops under the text rather than squeezing beside it. */}
        <div className="flex flex-col gap-4 p-6 pb-0 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-foreground text-lg font-semibold">{walk.length ? `${walk.length} waiting for you` : 'Nothing waiting for you'}</h2>
            <p className="text-muted-foreground mt-1 text-sm">
              {[
                walk.length ? 'Oldest first. Each opens beside its file, with the record its uploader gave it.' : '',
                settled ? `You have decided ${settled} so far.` : '',
                fourEyes && mine
                  ? `${mine} ${mine === 1 ? 'is' : 'are'} your own upload${mine === 1 ? '' : 's'} — with four-eyes on, someone else approves ${mine === 1 ? 'it' : 'them'}.`
                  : '',
                more > 0 ? `The oldest ${formatNumber(queue.data.items.length)} of ${formatNumber(queue.data.total)} are listed; the rest follow as these are decided.` : '',
              ]
                .filter(Boolean)
                .join(' ')}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {walk.length > 0 && (
              <Button className="whitespace-nowrap" onClick={() => select(walk[0]!)}>
                Start reviewing →
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={refresh} aria-label="Refresh the queue" title="Refresh">
              <RotateCcw size={14} aria-hidden />
            </Button>
          </div>
        </div>
        <div
          aria-hidden
          className="text-muted-foreground border-subtle mt-4 hidden grid-cols-[minmax(0,2.6fr)_minmax(0,1fr)_minmax(0,1.3fr)_7rem] gap-4 border-y px-6 py-2 text-xs font-semibold tracking-wide uppercase md:grid"
        >
          <span>Document</span>
          <span>Workspace</span>
          <span>Waiting since</span>
          <span />
        </div>
        <ul className="divide-border divide-y max-md:mt-3 max-md:border-t" aria-label="Waiting for review">
          {rows.map((row) => (
            <QueueLine
              key={row.id}
              row={row}
              own={ownOf(row)}
              typeLabel={documentTypeLabel(row.document_type, types)}
              workspace={workspaces.nameOf(row.workspace_id)}
              userId={userId}
              onOpen={select}
            />
          ))}
        </ul>
      </Card>
    </div>
  )
}

/* ------------------------------------------------------------- the queue */

/** A whole row that opens something — the kit's `Button` with the link look taken off, so the row's own layout shows. */
function RowButton({ className, ...props }: Omit<ButtonProps, 'variant' | 'size' | 'type'>) {
  return (
    <Button
      {...props}
      type="button"
      variant="link"
      className={cn('text-foreground justify-start text-left font-normal hover:no-underline focus-visible:ring-offset-0', className)}
    />
  )
}

/** "you", or another member — the envelope carries ids, and the member directory is the console's. */
const whoUploaded = (row: ProDocDocument, userId: string | null | undefined) => uploaderLabel(row.uploaded_by ?? '', userId)

/** One document in the full queue: what it is, where it is filed, how long it has waited and whose it is. */
function QueueLine({
  row,
  own,
  typeLabel,
  workspace,
  userId,
  onOpen,
}: {
  row: ProDocDocument
  own: boolean
  typeLabel: string
  workspace: string | null
  userId: string | null | undefined
  onOpen: (id: string) => void
}) {
  const since = row.review_requested_at ?? row.updated_at
  return (
    <li>
      <RowButton
        onClick={() => onOpen(row.id)}
        className="hover:bg-muted/60 focus-visible:bg-muted/60 grid w-full gap-x-4 gap-y-1 px-6 py-3.5 transition-colors focus-visible:outline-none md:grid-cols-[minmax(0,2.6fr)_minmax(0,1fr)_minmax(0,1.3fr)_7rem] md:items-center"
      >
        <span className="flex min-w-0 items-start gap-3">
          <FileMark mediaType={row.media_type} size="sm" />
          <span className="min-w-0">
            <span className="text-foreground block truncate text-sm font-medium" title={row.title}>
              {row.title}
            </span>
            <span className="text-muted-foreground block truncate text-xs">
              {typeLabel} · {mediaLabel(row.media_type)}
              {row.version_count > 1 ? ` · version ${row.version_count}` : ''}
            </span>
            {/* On a phone the columns fold into one line under the name. */}
            <span className="text-muted-foreground mt-0.5 block truncate text-xs md:hidden">
              {[workspace, when(since), own ? 'yours — someone else approves' : whoUploaded(row, userId)].filter(Boolean).join(' · ')}
            </span>
          </span>
        </span>
        <span className="text-muted-foreground truncate text-sm max-md:hidden">{workspace ?? '—'}</span>
        <span className="text-muted-foreground min-w-0 text-sm max-md:hidden">
          <span className="block truncate">
            {when(since)} · {whoUploaded(row, userId)}
          </span>
        </span>
        <span className="flex items-center justify-end gap-1 max-md:hidden">
          {own ? (
            <Badge size="sm">
              <UserRound size={11} aria-hidden />
              Yours
            </Badge>
          ) : (
            <span className="text-primary inline-flex items-center gap-0.5 text-sm font-medium">
              Review
              <ChevronRight size={14} aria-hidden />
            </span>
          )}
        </span>
      </RowButton>
    </li>
  )
}

/** A document in the pinned side list: the name, how long it has waited, and whose it is. */
function QueueRow({
  row,
  own,
  selected,
  userId,
  onOpen,
}: {
  row: ProDocDocument
  own: boolean
  selected: boolean
  userId: string | null | undefined
  onOpen: (id: string) => void
}) {
  return (
    <li>
      <RowButton
        className={cn('flex w-full items-start gap-2.5 rounded-xl px-2.5 py-2 transition-colors', selected ? 'bg-primary/5 ring-primary/40 ring-1' : 'hover:bg-muted')}
        aria-current={selected ? 'true' : undefined}
        onClick={() => onOpen(row.id)}
      >
        <FileMark mediaType={row.media_type} size="sm" />
        <span className="min-w-0 flex-1">
          <span className="text-foreground block truncate text-sm font-medium">{row.title}</span>
          <span className="text-muted-foreground block truncate text-xs">
            {when(row.review_requested_at ?? row.updated_at)}
            {own ? ' · yours' : ` · ${whoUploaded(row, userId)}`}
          </span>
        </span>
      </RowButton>
    </li>
  )
}

/* ------------------------------------------------------- the decision */

interface DecisionPanelProps {
  client: ProDocClient
  documentId: string
  /** The four-eyes walk, in order: what ‹ › and *Approve and next* move along. */
  walk: string[]
  /** The queue's answer: the service left this one out of the caller's walk. */
  ownByQueue: boolean
  fourEyes: boolean
  userId: string | null | undefined
  types: DocumentTypeOption[]
  nameOf: (id: string | null | undefined) => string | null
  onOpen: (id: string) => void
  onFinished: () => void
  onDecided: (doc: ProDocDocument) => void
  /** The review moved on without us — re-read the queue. */
  onStale: () => void
  onOpenDocument?: (document: ProDocDocument) => void
  /** The host's controls, first in the review bar: the way back and the queue toggle. */
  leading: ReactNode
}

/** One document, fresh for each one in the walk — its record, its current version, then the decision. */
function DecisionPanel(props: DecisionPanelProps) {
  const { client, documentId, onOpen, onFinished, onOpenDocument, leading } = props
  const doc = useLoad(() => client.documents.get(documentId), [client, documentId])
  // The version a decision judges is the current one; its uploader is who four-eyes asks about.
  const versions = useLoad(() => client.documents.versions.list(documentId), [client, documentId])

  if (doc.loading && !doc.data) {
    return (
      <SkeletonRegion label="Loading this document" className="space-y-6">
        <Skeleton className="h-20 w-full rounded-2xl" />
        <div className="grid gap-6 xl:grid-cols-2">
          <Skeleton className="h-[60vh] rounded-2xl" />
          <Card className="space-y-3 p-6">
            <SkeletonText w="w-40" />
            <SkeletonText />
            <SkeletonText w="w-2/3" />
          </Card>
        </div>
      </SkeletonRegion>
    )
  }

  const back = (
    <Button variant="outline" onClick={onFinished}>
      Back to the queue
    </Button>
  )

  const d = doc.data
  if (!d) {
    // Out of sight is NOT_FOUND, never FORBIDDEN (constitution §3): one sentence for both.
    const missing = !doc.error || /not found|does not exist/i.test(doc.error)
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">{leading}</div>
        <EmptyState
          title={missing ? 'Document not found' : 'This document could not be loaded'}
          description={missing ? 'It may have been archived, or it is outside the workspaces you see.' : doc.error}
          action={
            missing ? (
              back
            ) : (
              <Button variant="outline" onClick={() => void doc.reload()}>
                Try again
              </Button>
            )
          }
        />
      </div>
    )
  }

  if (d.status !== 'stored' || d.review_state !== 'needs_review') {
    const why = d.status !== 'stored' ? 'is archived' : `is ${reviewLabel(d.review_state).toLowerCase()}`
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">{leading}</div>
        <EmptyState
          title="Nothing to review here"
          description={`“${d.title}” ${why}, so it is not waiting for a decision. A new version of it would come back here.`}
          action={
            <div className="flex flex-wrap justify-center gap-2">
              {back}
              {onOpenDocument && (
                <Button variant="ghost" onClick={() => onOpenDocument(d)}>
                  Open the document
                </Button>
              )}
            </div>
          }
        />
      </div>
    )
  }

  const current = versions.data?.find((v) => v.is_current || v.id === d.current_version_id) ?? d.current_version
  return (
    <Decision
      {...props}
      doc={d}
      current={current}
      onOpen={onOpen}
      onReload={() => {
        void doc.reload()
        void versions.reload()
      }}
    />
  )
}

const REJECT_REASONS = [
  { value: 'copy', label: 'It is a copy of a document already here', text: 'A copy of a document already in ProDoc.' },
  { value: 'not_ours', label: 'It is not this organisation’s', text: 'Not addressed to this organisation.' },
  { value: 'unreadable', label: 'It is too poor to read', text: 'Unreadable — a better copy is needed.' },
  { value: 'record', label: 'Its record does not match the file', text: 'The title, type or date does not match the file.' },
  { value: 'other', label: 'Something else', text: '' },
] as const

type RejectPreset = (typeof REJECT_REASONS)[number]['value']

const VERSION_REASON: Record<string, string> = {
  initial: 'first upload',
  replacement: 'a replacement',
  resubmission: 're-submitted after a rejection',
  duplicate_linked: 'linked from a duplicate upload',
  app_supersede: 'superseded by its app',
  imported: 'imported from its app, at its original time',
}

const shown = (v: CustomValue | undefined): string => (v === null || v === undefined ? '' : typeof v === 'boolean' ? (v ? 'yes' : 'no') : String(v))

/** A newer version became current after the reviewer opened this one (the decision named the version it judged). */
const isNewerVersion = (e: unknown) => e instanceof ApiError && e.code === 'CONFLICT' && e.reason === 'version_conflict'

/** Why a decision was refused, in the words a reviewer can act on. */
function decisionError(e: unknown): string {
  if (isFourEyes(e)) return 'You uploaded the version under review, and this organisation uses four-eyes approval — someone else decides it.'
  if (isNewerVersion(e)) return 'A newer version was uploaded after you opened this document, and it is shown now. Check it before you decide.'
  if (isIllegalTransition(e)) return 'This document is no longer waiting for review: someone else decided it, or a new version arrived. The queue has been read again.'
  if (e instanceof ApiError && e.code === 'FORBIDDEN') return 'Your ProDoc role does not include approving or rejecting documents.'
  return refusalText(e)
}

function Decision({
  client,
  doc,
  current,
  walk,
  ownByQueue,
  fourEyes,
  userId,
  types,
  nameOf,
  onOpen,
  onFinished,
  onDecided,
  onStale,
  onOpenDocument,
  onReload,
  leading,
}: DecisionPanelProps & { doc: ProDocDocument; current: DocumentVersion | undefined; onReload: () => void }) {
  const { toast } = useToast()
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null)
  const [error, setError] = useState('')
  const [rejecting, setRejecting] = useState(false)
  const [rejectError, setRejectError] = useState('')
  // One decision in flight, whatever the keyboard repeats: the guard is a ref
  // because a second Ctrl + Enter arrives before React re-renders `busy`.
  const deciding = useRef(false)

  const own = ownByQueue || isOwnVersion({ ...doc, current_version: current }, userId)
  const blocked = fourEyes && own
  const tooLong = comment.length > MAX_REVIEW_COMMENT
  const at = walk.indexOf(doc.id)
  const prevId = at > 0 ? walk[at - 1] : undefined
  const nextId = at >= 0 ? walk[at + 1] : walk.find((id) => id !== doc.id)
  const workspace = nameOf(doc.workspace_id)
  const versionNo = current?.version_no ?? doc.version_count
  // An app is said with whom it acted for (FR-40); four-eyes treats a version an app stored for a member as that member's.
  const uploader = current ? versionUploaderLabel(current, userId) : uploaderLabel(doc.uploaded_by ?? '', userId)

  const approve = async () => {
    if (deciding.current || blocked || tooLong || rejecting) return
    deciding.current = true
    const target = nextId
    setBusy('approve')
    setError('')
    try {
      // The version on screen is the one judged; a newer one since is refused, not approved unseen.
      const next = await client.documents.review.approve(doc.id, { comment, version_id: doc.current_version_id })
      onDecided(next)
      toast(`Approved “${doc.title}”.`)
      if (target) onOpen(target)
      else onFinished()
    } catch (e) {
      // A refusal stays on the page, beside the thing that refused.
      setError(decisionError(e))
      if (isIllegalTransition(e)) onStale()
      if (isNewerVersion(e)) onReload()
    } finally {
      deciding.current = false
      setBusy(null)
    }
  }

  const reject = async (text: string) => {
    if (deciding.current) return
    deciding.current = true
    const target = nextId
    setBusy('reject')
    setRejectError('')
    try {
      const next = await client.documents.review.reject(doc.id, { comment: text, version_id: doc.current_version_id })
      onDecided(next)
      setRejecting(false)
      toast(`Rejected “${doc.title}”.`)
      if (target) onOpen(target)
      else onFinished()
    } catch (e) {
      setRejectError(decisionError(e))
      if (isIllegalTransition(e)) onStale()
      if (isNewerVersion(e)) onReload()
    } finally {
      deciding.current = false
      setBusy(null)
    }
  }

  const custom = Object.entries(doc.custom_fields ?? {})

  // W24 (FR-56): J / K walk the queue beside Alt + ← / → — never from the comment box, never while rejecting.
  useQueueKeys({ next: nextId ? () => onOpen(nextId) : null, prev: prevId ? () => onOpen(prevId) : null, enabled: !rejecting })

  return (
    <div>
      <KeyboardShortcuts
        enabled={!rejecting}
        onApprove={() => void approve()}
        onPrev={prevId ? () => onOpen(prevId) : undefined}
        onNext={nextId ? () => onOpen(nextId) : undefined}
      />

      {/* The review bar: where you are and the decision — always in view, never on top of the file. */}
      <div className="border-subtle bg-background/95 sticky top-14 z-20 mb-6 rounded-2xl border px-4 py-3 shadow-sm backdrop-blur">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
            {leading}
            {at >= 0 && (
              <span className="flex items-center gap-1">
                <Button variant="ghost" size="sm" disabled={!prevId} onClick={() => prevId && onOpen(prevId)} aria-label="Previous document" title="Previous (K, or Alt + ←)">
                  <ChevronLeft size={16} aria-hidden />
                </Button>
                <span className="text-foreground shrink-0 font-medium">
                  {at + 1} of {walk.length} <span className="text-muted-foreground font-normal">to review</span>
                </span>
                <Button variant="ghost" size="sm" disabled={!nextId} onClick={() => nextId && onOpen(nextId)} aria-label="Next document" title="Next (J, or Alt + →)">
                  <ChevronRight size={16} aria-hidden />
                </Button>
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setRejectError('')
                setRejecting(true)
              }}
              disabled={busy !== null}
            >
              Reject…
            </Button>
            <Button
              size="sm"
              onClick={() => void approve()}
              disabled={blocked || tooLong || busy !== null}
              loading={busy === 'approve'}
              loadingText="Approving…"
              title={blocked ? 'You uploaded this version — someone else approves it.' : `Approve${nextId ? ' and open the next' : ''} (${SHORTCUT})`}
            >
              {nextId ? 'Approve and next →' : 'Approve'}
            </Button>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex min-w-0 items-center gap-2">
            <FileMark mediaType={doc.media_type} size="sm" />
            <h2 className="text-foreground truncate text-lg font-semibold" title={doc.title}>
              {doc.title}
            </h2>
            <ReviewBadge state={doc.review_state} size="sm" />
          </div>
          {blocked ? (
            <span className={cn('flex items-center gap-1 text-sm whitespace-nowrap', TONE_TEXT.warning)}>
              <TriangleAlert size={14} aria-hidden />
              You uploaded this version — someone else approves it.
            </span>
          ) : (
            <span className={cn('flex items-center gap-1 text-sm', TONE_TEXT.success)}>
              <CircleCheck size={14} aria-hidden />
              {SHORTCUT} approves
            </span>
          )}
        </div>
      </div>

      {error && (
        <Alert tone="error" className="mb-6">
          {error}
        </Alert>
      )}

      {/* Side by side when the pane — not the window — is wide enough, the file
          the larger: it is what the record is checked against. */}
      <div className="@container">
        <div className="grid items-start gap-6 @3xl:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
          <Card padding="md" className="@3xl:sticky @3xl:top-48 @3xl:max-h-[calc(100vh-13rem)] @3xl:overflow-auto">
            <Preview client={client} doc={doc} />
          </Card>

          <div className="space-y-6">
            {blocked && (
              <Alert tone="info">
                You uploaded the version under review, and this organisation uses four-eyes approval, so someone else approves it. It stays in the
                queue for them.
              </Alert>
            )}

            <Card>
              <CardHeader title="The record" description="What its uploader said this document is. Compare it with the file." />
              <CardBody>
                <DescriptionList>
                  <Detail label="Title" value={doc.title} wide />
                  <Detail label="Type" value={documentTypeLabel(doc.document_type, types)} />
                  <Detail label="Document date" value={doc.document_date ? formatDate(doc.document_date) : null} emptyLabel="Not set" />
                  <Detail label="Workspace" value={workspace} emptyLabel="—" />
                  <Detail label="Owner app" value={doc.owner_app} />
                  {doc.source_ref && <Detail label="Source" value={doc.source_ref} />}
                  <Detail label="Notes" value={doc.notes} emptyLabel="None" wide />
                  {custom.map(([key, value]) => (
                    <Detail key={key} label={key.replace(/_/g, ' ')} value={shown(value)} emptyLabel="Not set" />
                  ))}
                </DescriptionList>
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="The file under review" description="A decision judges this version; a later one comes back for its own." />
              <CardBody className="space-y-3">
                <DescriptionList>
                  <Detail label="Version" value={`Version ${versionNo}${current?.reason && VERSION_REASON[current.reason] ? ` — ${VERSION_REASON[current.reason]}` : ''}`} wide />
                  <Detail label="File name" value={doc.file_name} wide />
                  <Detail label="Kind" value={mediaLabel(doc.media_type)} />
                  <Detail label="Size" value={formatBytes(doc.size_bytes)} />
                  {doc.page_count !== null && doc.page_count !== undefined && <Detail label="Pages" value={String(doc.page_count)} />}
                  {doc.encrypted && <Detail label="Encrypted" value="Yes — download it and open it with its password" />}
                  <Detail label="Uploaded by" value={uploader === 'you' ? 'You' : uploader === 'another member' ? 'Another member' : uploader} />
                  <Detail label="Waiting since" value={formatDateTime(doc.review_requested_at ?? doc.updated_at)} />
                </DescriptionList>
                {doc.review_comment && (
                  <p className="text-muted-foreground text-sm">
                    The last decision’s comment{doc.reviewed_at ? `, ${formatDate(doc.reviewed_at)}` : ''}: “{doc.review_comment}”
                  </p>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Your decision" description={`Approve or reject from the bar above. ${SHORTCUT} approves and opens the next.`} />
              <CardBody>
                <Textarea
                  label="Comment"
                  labelAside={<span className="text-muted-foreground text-xs">{`${formatNumber(comment.length)} / ${formatNumber(MAX_REVIEW_COMMENT)}`}</span>}
                  hint="Optional. Recorded with the decision; whoever uploaded it reads it."
                  rows={3}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  error={tooLong ? `Up to ${formatNumber(MAX_REVIEW_COMMENT)} characters.` : null}
                />
              </CardBody>
            </Card>

            {onOpenDocument && (
              <Button variant="link" onClick={() => onOpenDocument(doc)}>
                Open the whole record — its versions and history
              </Button>
            )}
          </div>
        </div>
      </div>

      <RejectDialog
        open={rejecting}
        title={doc.title}
        initialNote={comment}
        busy={busy === 'reject'}
        error={rejectError}
        onClose={() => {
          setRejecting(false)
          setRejectError('')
        }}
        onReject={(text) => void reject(text)}
      />
    </div>
  )
}

/** Ctrl/⌘ + Enter approves; Alt + ← / → walks the queue. Registered once; the latest handlers are read at the key press. */
function KeyboardShortcuts({ enabled, onApprove, onPrev, onNext }: { enabled: boolean; onApprove: () => void; onPrev?: () => void; onNext?: () => void }) {
  const latest = useRef({ enabled, onApprove, onPrev, onNext })
  latest.current = { enabled, onApprove, onPrev, onNext }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const h = latest.current
      if (!h.enabled) return
      // Alt + an arrow moves by word inside a text field (Option on macOS), so it walks the queue only from outside one.
      const target = e.target instanceof Element ? e.target : null
      const typing = !!target && (!!target.closest('input, textarea, select') || (target instanceof HTMLElement && target.isContentEditable))
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault()
        h.onApprove()
      } else if (typing) {
        return
      } else if (e.altKey && e.key === 'ArrowLeft' && h.onPrev) {
        e.preventDefault()
        h.onPrev()
      } else if (e.altKey && e.key === 'ArrowRight' && h.onNext) {
        e.preventDefault()
        h.onNext()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
  return null
}

/**
 * Reject, with the common reasons one click away and a note for the uploader.
 * Both are optional — "no comment" is a decision too (plan §5.2) — and what is
 * said is recorded with the decision and sent to whoever uploaded it.
 */
function RejectDialog({
  open,
  title,
  initialNote,
  busy,
  error,
  onClose,
  onReject,
}: {
  open: boolean
  title: string
  initialNote: string
  busy: boolean
  error: string
  onClose: () => void
  onReject: (comment: string) => void
}) {
  const [choice, setChoice] = useState<RejectPreset | null>(null)
  const [note, setNote] = useState(initialNote)

  // Each opening starts from the decision's comment, never from an abandoned one.
  useEffect(() => {
    if (open) {
      setChoice(null)
      setNote(initialNote)
    }
    // Only the opening is the question; the comment as it was then is the start.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const preset = REJECT_REASONS.find((r) => r.value === choice)
  const text = [preset?.text ?? '', note.trim()].filter(Boolean).join(' ')
  const tooLong = text.length > MAX_REVIEW_COMMENT

  return (
    <Dialog open={open} onClose={onClose} title={`Reject “${title}”?`} error={error || null}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (!tooLong) onReject(text)
        }}
      >
        <RadioGroup legend="Why? (optional)" options={REJECT_REASONS.map((r) => ({ value: r.value, label: r.label }))} value={choice} onChange={setChoice} />
        <Textarea
          label="Tell the uploader"
          hint="Optional — recorded with the decision, and sent to whoever uploaded it."
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          error={tooLong ? `Up to ${formatNumber(MAX_REVIEW_COMMENT)} characters, the reason included.` : null}
        />
        <p className="text-muted-foreground text-sm">
          It stays in ProDoc marked Rejected, with what you said — nothing is deleted. It comes back for review when a new version of it is
          uploaded.
        </p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Keep reviewing
          </Button>
          <Button type="submit" variant="destructive" loading={busy} loadingText="Rejecting…" disabled={tooLong}>
            Reject document
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
